// Shared by Cloud Functions and the frontend (imported there as @shared).
// Keep this file free of Node- or browser-only APIs.
//
// Each league stores a full copy of its settings (built from one of these
// presets plus admin overrides), so changing a preset later never changes
// a league that already exists.

// Every conference, generated or uploaded, must have this many teams.
export const CONFERENCE_MIN = 8;
export const CONFERENCE_MAX = 18;
// Conference tournament field sizes an admin can choose. A conference with
// fewer teams than the chosen size uses the largest size it can fill.
export const CONFERENCE_TOURNAMENT_SIZES = [8, 12, 16];
// Pro playoff field sizes an admin can choose (no larger than half the
// league). A field that isn't a power of 2 gives the top seeds byes.
export const PLAYOFF_SIZES = [4, 8, 12, 16];

export const LEAGUE_TYPES = {
  college: {
    label: "College",
    teamCount: 150,
    conferenceCount: 12,
    rosterSize: 15,
    gamesPerTeam: 30,
    conferenceGames: 18,
    gameDaysPerRealDay: 1,
    minutesPerGame: 40,
    // pace: possessions per team per game. bonusFouls: team fouls per period
    // before non-shooting fouls give free throws. homeEdge: added to the
    // home team's shot make probability.
    game: {pace: 68, foulOutLimit: 5, bonusFouls: 7, homeEdge: 0.045},
    // Ratings are 1-100. ratingSpread: player-to-player SD; prestigeEffect:
    // rating gap between prestige 1 and prestige 5 teams' players, halved.
    // Calibrated with scripts/calibrate.js and full simulated seasons: a
    // top-15% team beats an average team about 77% of the time at a
    // neutral site; season win% SD about 0.205 with real schedules.
    talent: {ratingMean: 50, ratingSpread: 9, prestigeEffect: 7.5},
    // How much lineup chemistry changes shooting, defense and turnovers
    // (see TUNING.chem* in src/sim/engine.js); 0 = off. An average team
    // with great chemistry beats a good team with poor chemistry about 40%
    // of the time (vs 23% with equal chemistry).
    chemistryStrength: 0.8,
    career: {model: "eligibility", years: 4, redshirt: true},
    postseason: {
      format: "bracket",
      conferenceTournaments: true,
      conferenceTournamentSize: 8,
      fieldSize: 64,
    },
    useQuads: true,
  },
  pro: {
    label: "Pro",
    teamCount: 30,
    conferenceCount: 2,
    rosterSize: 13,
    gamesPerTeam: 60,
    conferenceGames: 30,
    gameDaysPerRealDay: 2,
    minutesPerGame: 48,
    game: {pace: 98, foulOutLimit: 6, bonusFouls: 5, homeEdge: 0.012},
    // Calibrated: win% SD about 0.14 (NBA-like parity).
    talent: {ratingMean: 60, ratingSpread: 5.5, prestigeEffect: 0},
    // Lower than college: with pro talent this close, full-strength
    // chemistry would make the weaker team the favorite in the upset test.
    // At 0.3 the average team with great chemistry wins about 45%.
    chemistryStrength: 0.3,
    career: {
      model: "age",
      minAge: 19,
      peakAge: 27,
      declineAge: 31,
      maxAge: 38,
    },
    postseason: {
      format: "series",
      conferenceTournaments: false,
      fieldSize: 16,
      seriesLengths: [5, 7, 7, 7],
    },
    useQuads: false,
    // Contracts and free agency (shared/contracts.js): a hard cap, NBA-like
    // dollars; rosters of rosterMin-rosterMax players.
    money: {
      cap: 140000000,
      minSalary: 1200000,
      maxShare: 0.3,
      rosterMin: 12,
      rosterMax: 15,
    },
  },
};

/**
 * Builds a league's settings from a preset plus admin overrides.
 * Only keys that exist in the preset can be overridden.
 * @param {string} type "college" or "pro"
 * @param {object} overrides
 * @return {object} settings, including `type`
 * @throws {Error} if the type is unknown or the settings are invalid
 */
export function buildLeagueSettings(type, overrides = {}) {
  const preset = LEAGUE_TYPES[type];
  if (!preset) throw new Error(`Unknown league type: ${type}`);

  const settings = structuredClone(preset);
  for (const [key, value] of Object.entries(overrides ?? {})) {
    if (!(key in preset) || key === "label") {
      throw new Error(`Unknown league setting: ${key}`);
    }
    if (isPlainObject(preset[key])) {
      if (!isPlainObject(value)) {
        throw new Error(`League setting ${key} must be an object`);
      }
      for (const [subKey, subValue] of Object.entries(value)) {
        if (!(subKey in preset[key])) {
          throw new Error(`Unknown league setting: ${key}.${subKey}`);
        }
        settings[key][subKey] = subValue;
      }
    } else {
      settings[key] = value;
    }
  }

  const errors = validateLeagueSettings(settings);
  if (errors.length) throw new Error(errors.join(" "));
  return {type, ...settings};
}

/**
 * @param {object} s settings to check
 * @return {string[]} problems found; empty when valid
 */
export function validateLeagueSettings(s) {
  const errors = [];
  const int = (name, value, min, max) => {
    if (!Number.isInteger(value) || value < min || value > max) {
      errors.push(`${name} must be a whole number from ${min} to ${max}.`);
    }
  };

  int("teamCount", s.teamCount, CONFERENCE_MIN, 400);
  int("conferenceCount", s.conferenceCount, 1,
      Math.max(1, Math.floor(s.teamCount / CONFERENCE_MIN)));
  if (Number.isInteger(s.teamCount) && Number.isInteger(s.conferenceCount) &&
      s.conferenceCount > 0) {
    const sizes = conferenceSizes(s.teamCount, s.conferenceCount);
    if (Math.min(...sizes) < CONFERENCE_MIN ||
        Math.max(...sizes) > CONFERENCE_MAX) {
      errors.push(`${s.teamCount} teams in ${s.conferenceCount} ` +
        `conferences gives conferences of ${Math.min(...sizes)}-` +
        `${Math.max(...sizes)} teams; each must have ${CONFERENCE_MIN}-` +
        `${CONFERENCE_MAX}.`);
    }
  }
  int("rosterSize", s.rosterSize, 10, 20);
  int("gamesPerTeam", s.gamesPerTeam, 2, 120);
  int("conferenceGames", s.conferenceGames, 0, s.gamesPerTeam);
  int("gameDaysPerRealDay", s.gameDaysPerRealDay, 1, 4);
  int("minutesPerGame", s.minutesPerGame, 20, 60);
  int("game.pace", s.game?.pace, 40, 130);
  int("game.foulOutLimit", s.game?.foulOutLimit, 3, 10);
  int("game.bonusFouls", s.game?.bonusFouls, 1, 20);
  const edge = s.game?.homeEdge;
  if (typeof edge !== "number" || edge < 0 || edge > 0.1) {
    errors.push("game.homeEdge must be a number from 0 to 0.1.");
  }
  const cs = s.chemistryStrength;
  if (typeof cs !== "number" || cs < 0 || cs > 3) {
    errors.push("chemistryStrength must be a number from 0 to 3.");
  }
  for (const key of ["ratingMean", "ratingSpread", "prestigeEffect"]) {
    const v = s.talent?.[key];
    if (typeof v !== "number" || v < 0 || v > 100) {
      errors.push(`talent.${key} must be a number from 0 to 100.`);
    }
  }

  const field = s.postseason?.fieldSize;
  const series = s.postseason?.format === "series";
  if (series) {
    // Pro playoffs: any field; the top seeds get byes to fill the bracket.
    int("postseason.fieldSize", field, 2, Math.max(2, s.teamCount));
  } else if (!isPowerOfTwo(field) || field < 2 || field > s.teamCount) {
    errors.push("postseason.fieldSize must be a power of 2 " +
        "no larger than teamCount.");
  }
  if (series) {
    const lengths = s.postseason.seriesLengths;
    const rounds = Number.isInteger(field) && field >= 2 ?
      playoffRounds(field) : 0;
    if (!Array.isArray(lengths) || lengths.length !== rounds ||
        lengths.some((n) => !Number.isInteger(n) || n < 1 || n % 2 === 0)) {
      errors.push(`postseason.seriesLengths must list ${rounds} odd ` +
          "series lengths, one per round.");
    }
  } else if (s.postseason?.format !== "bracket") {
    errors.push("postseason.format must be \"bracket\" or \"series\".");
  }
  if (s.postseason?.conferenceTournaments &&
      !CONFERENCE_TOURNAMENT_SIZES.includes(
          s.postseason.conferenceTournamentSize)) {
    errors.push("postseason.conferenceTournamentSize must be one of " +
      `${CONFERENCE_TOURNAMENT_SIZES.join(", ")}.`);
  }
  return errors;
}

/**
 * Splits teams across conferences as evenly as possible.
 * conferenceSizes(150, 12) -> [13, 13, 13, 13, 13, 13, 12, ...].
 * @param {number} teamCount
 * @param {number} conferenceCount
 * @return {number[]} size of each conference, largest first
 */
export function conferenceSizes(teamCount, conferenceCount) {
  const base = Math.floor(teamCount / conferenceCount);
  const extra = teamCount % conferenceCount;
  return Array.from({length: conferenceCount},
      (_, i) => base + (i < extra ? 1 : 0));
}

/**
 * The conference tournament field a conference of this size uses: the
 * league's chosen size, or the largest allowed size the conference fills.
 * @param {number} conferenceTeams
 * @param {number} chosenSize league postseason.conferenceTournamentSize
 * @return {number}
 */
export function conferenceTournamentField(conferenceTeams, chosenSize) {
  const fits = CONFERENCE_TOURNAMENT_SIZES
      .filter((n) => n <= Math.min(conferenceTeams, chosenSize));
  return fits.length ? Math.max(...fits) : conferenceTeams;
}

/**
 * @param {*} value
 * @return {boolean}
 */
function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * @param {number} n
 * @return {number} smallest power of 2 >= n
 */
export function bracketSize(n) {
  let size = 1;
  while (size < n) size *= 2;
  return size;
}

/**
 * Rounds a pro playoff field plays (one series length each): a 12-team
 * field fills a 16-team bracket, so 4.
 * @param {number} fieldSize
 * @return {number}
 */
export function playoffRounds(fieldSize) {
  return Math.log2(bracketSize(fieldSize));
}

/**
 * @param {*} n
 * @return {boolean}
 */
export function isPowerOfTwo(n) {
  return Number.isInteger(n) && n > 0 && (n & (n - 1)) === 0;
}
